import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Calendar } from 'lucide-react';
import { Button } from '../ui/button';

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
    setActiveDropdown(null);
  }, [location]);

  const aboutLinks = [
    { to: '/about/background', label: 'Background' },
    { to: '/about/board', label: 'Board of Directors' },
    { to: '/about/collaborations', label: 'Collaborations' },
    { to: '/about/projects', label: 'Projects' },
    { to: '/about/department-overview', label: 'Department Overview' },
  ];

  const mainLinks = [
    { to: '/', label: 'Home' },
    { to: '/departments', label: 'Departments' },
    { to: '/executive-health', label: 'Executive Health' },
    { to: '/doctors', label: 'Our Doctors' },
    { to: '/find-doctor', label: 'Find a Doctor' },
    { to: '/news', label: 'News' },
    { to: '/contact', label: 'Contact' },
  ];

  // Determine if we should use transparent mode
  const useTransparent = isHomePage && !isScrolled;

  // Don't render navbar on homepage when not scrolled (hero has its own nav)
  if (isHomePage && !isScrolled) {
    return null;
  }

  return (
    <header className={`w-full ${isHomePage ? 'absolute top-0 left-0 right-0 z-50' : 'relative'}`} data-testid="navbar">
      {/* Branding Section - Only show when scrolled or not on homepage */}
      {(!isHomePage || isScrolled) && (
        <div className={`transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-white border-b border-slate-100'}`}>
          <div className="container-custom py-4">
            <div className="flex flex-col items-center justify-center">
              <Link to="/" className="flex flex-col items-center group" data-testid="navbar-logo">
                <img 
                  src="https://customer-assets.emergentagent.com/job_43b6699e-7b3c-4d06-ad8e-7888613bee79/artifacts/5oi0txpb_Logooooo%20BH%20.png" 
                  alt="Baobab Hospital Logo" 
                  className="w-16 h-16 md:w-20 md:h-20 object-contain mb-2"
                />
                <h1 className="font-display text-2xl md:text-3xl font-semibold text-primary tracking-wide text-center" style={{ fontFamily: "'Cinzel', serif" }}>
                  BAOBAB HOSPITAL
                </h1>
                <p className="text-slate-500 text-xs mt-1 font-body tracking-widest uppercase">
                  Your Life is in Safe Hands
                </p>
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Navigation Bar */}
      <nav className={`transition-all duration-300 ${
        useTransparent 
          ? 'bg-transparent' 
          : isScrolled 
            ? 'fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-md shadow-md z-50' 
            : 'bg-white border-b border-slate-100'
      }`}>
        <div className="container-custom">
          <div className="flex items-center justify-between h-16">
            {/* Compact Logo for Scrolled State */}
            <Link to="/" className={`flex items-center gap-2 transition-all duration-300 ${isScrolled && !isHomePage ? 'opacity-100' : isScrolled ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
              <img 
                src="https://customer-assets.emergentagent.com/job_43b6699e-7b3c-4d06-ad8e-7888613bee79/artifacts/5oi0txpb_Logooooo%20BH%20.png" 
                alt="Baobab Hospital Logo" 
                className="w-10 h-10 object-contain"
              />
              <span className="font-heading font-semibold text-primary text-lg hidden md:block" style={{ fontFamily: "'Cinzel', serif" }}>Baobab Hospital</span>
            </Link>

            {/* Desktop Navigation */}
            <div className={`hidden lg:flex items-center justify-center flex-1 space-x-0 ${useTransparent ? '' : ''}`}>
              <Link to="/" className={`px-3 py-2 font-medium transition-colors whitespace-nowrap ${useTransparent ? 'text-white hover:text-white/80' : 'text-slate-700 hover:text-primary'}`} data-testid="nav-home">
                Home
              </Link>

              {/* About Dropdown */}
              <div 
                className="relative group"
                onMouseEnter={() => setActiveDropdown('about')}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                <button 
                  className={`flex items-center gap-1 px-3 py-2 font-medium transition-colors whitespace-nowrap ${useTransparent ? 'text-white hover:text-white/80' : 'text-slate-700 hover:text-primary'}`}
                  data-testid="nav-about-dropdown"
                >
                  About
                  <ChevronDown className={`w-4 h-4 transition-transform ${activeDropdown === 'about' ? 'rotate-180' : ''}`} />
                </button>
                <div className={`absolute top-full left-0 w-56 bg-white rounded-lg shadow-float border border-slate-100 py-2 transition-all duration-200 ${
                  activeDropdown === 'about' ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'
                }`}>
                  {aboutLinks.map((link) => (
                    <Link
                      key={link.to}
                      to={link.to}
                      className="block px-4 py-2.5 text-slate-600 hover:text-primary hover:bg-primary-50 transition-colors"
                      data-testid={`nav-about-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {link.label}
                    </Link>
                  ))}
                </div>
              </div>

              {mainLinks.slice(1).map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`px-3 py-2 font-medium transition-colors whitespace-nowrap ${useTransparent ? 'text-white hover:text-white/80' : 'text-slate-700 hover:text-primary'}`}
                  data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* Book Appointment Button */}
            <Link to="/book-appointment" className="hidden lg:block" data-testid="nav-book-appointment">
              <Button className={`rounded-lg px-6 py-2 font-medium shadow-sm hover:shadow-md transition-all ${
                useTransparent 
                  ? 'bg-white text-primary hover:bg-white/90' 
                  : 'bg-primary hover:bg-primary-700 text-white'
              }`}>
                <Calendar className="w-4 h-4 mr-2" />
                Book Appointment
              </Button>
            </Link>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className={`lg:hidden p-2 transition-colors ${useTransparent ? 'text-white' : 'text-slate-700 hover:text-primary'}`}
              data-testid="navbar-mobile-toggle"
              aria-label="Toggle menu"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div className={`lg:hidden transition-all duration-300 ${isOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0 overflow-hidden'}`}>
          <div className="bg-white border-t border-slate-100 shadow-lg">
            <div className="container-custom py-4 space-y-2">
              <Link to="/" className="block px-4 py-3 text-slate-700 hover:text-primary hover:bg-primary-50 rounded-lg font-medium transition-colors" data-testid="mobile-nav-home">
                Home
              </Link>

              {/* Mobile About Section */}
              <div>
                <button
                  onClick={() => setActiveDropdown(activeDropdown === 'about-mobile' ? null : 'about-mobile')}
                  className="flex items-center justify-between w-full px-4 py-3 text-slate-700 hover:text-primary hover:bg-primary-50 rounded-lg font-medium transition-colors"
                  data-testid="mobile-nav-about"
                >
                  About
                  <ChevronDown className={`w-4 h-4 transition-transform ${activeDropdown === 'about-mobile' ? 'rotate-180' : ''}`} />
                </button>
                <div className={`overflow-hidden transition-all duration-200 ${activeDropdown === 'about-mobile' ? 'max-h-96' : 'max-h-0'}`}>
                  <div className="pl-4 py-2 space-y-1">
                    {aboutLinks.map((link) => (
                      <Link
                        key={link.to}
                        to={link.to}
                        className="block px-4 py-2.5 text-slate-600 hover:text-primary hover:bg-primary-50 rounded-lg transition-colors"
                      >
                        {link.label}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>

              {mainLinks.slice(1).map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="block px-4 py-3 text-slate-700 hover:text-primary hover:bg-primary-50 rounded-lg font-medium transition-colors"
                  data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {link.label}
                </Link>
              ))}

              <div className="pt-4 border-t border-slate-100">
                <Link to="/book-appointment" data-testid="mobile-nav-book">
                  <Button className="w-full bg-primary hover:bg-primary-700 text-white rounded-lg py-3 font-medium shadow-sm">
                    <Calendar className="w-4 h-4 mr-2" />
                    Book Appointment
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </nav>
    </header>
  );
};

export default Navbar;
